import React, { useEffect, useState, useRef, useMemo } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { supabase } from "@/lib/supabase-client";
import { ThemedText } from "@/components/ThemedText";
import {
  TimecardCreateForm,
  TimecardCreateFormRef,
  TimecardPayload,
} from "@/components/screens/timecard/TimecardCreateForm";
import { CreateHeader } from "@/components/screens/shared";
import { timecardService } from "@/services/timecardService";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";

import Head from "expo-router/head";
import { useTheme } from "@/hooks/useTheme";
import { getTimecardStatusOptions } from "@/config/tableConfigs/timecardTableConfig";
import { getSaveBehavior } from "@/lib/saveSettings";

type TimecardMeta = any;

export default function NewTimecardPage() {
  const params = useLocalSearchParams();
  const toast = useToast();
  const router = useRouter();
  const { tenantId } = useAuth();
  const { colors } = useTheme();
  const formRef = useRef<TimecardCreateFormRef>(null);
  const [timecardMeta, setTimecardMeta] = useState<TimecardMeta | null>(null);
  const [localDraft, setLocalDraft] = useState<TimecardPayload>();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Header fields state
  const [userId, setUserId] = useState(typeof params.user_id === "string" ? params.user_id : "");
  const [status, setStatus] = useState("PENDING");

  // Get status options with current theme colors
  const statusOptions = useMemo(() => getTimecardStatusOptions(colors), [colors]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data, error } = await supabase.from("tenants").select("meta_data").eq("id", tenantId).single();
      if (error) throw error;
      setTimecardMeta((data as any)?.meta_data ?? null);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load timecard settings.");
      toast.showToast({ title: "Failed to fetch tenant data", message: e.message, toastType: "error" });
      setTimecardMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setUserId("");
    setStatus("PENDING");
    formRef.current?.handleReset();
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    formRef.current?.updateStatus(newStatus as "PENDING" | "APPROVED" | "REJECTED");
  };

  if (loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>Loading timecard settings…</ThemedText>
      </ScrollView>
    );
  }

  if (err || timecardMeta == null) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText type="defaultSemiBold">Something went wrong</ThemedText>
        <ThemedText style={{ opacity: 0.8, marginTop: 6 }}>{err ?? "Unknown error"}</ThemedText>
        <Button text="Retry" icon="refresh" style={{ marginTop: 12, borderRadius: 14, padding: 10 }} onPress={load} />
      </ScrollView>
    );
  }

  return (
    <>
      <Head>
        <title>NEXUS New Timecard</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="timecard"
          name=""
          nameEditable={false}
          namePlaceholder="Select user"
          dirty={dirty}
          status={status}
          onStatusChange={handleStatusChange}
          statusOptions={statusOptions}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
        />
        <ScrollView contentContainerStyle={{ flex: 1, padding: 16 }}>
          <TimecardCreateForm
            ref={formRef}
            initialTimecard={{
              user_id: userId,
              project_id: typeof params.project_id === "string" ? params.project_id : "",
              shift_id: typeof params.shift_id === "string" ? params.shift_id : "",
              status: status as "PENDING" | "APPROVED" | "REJECTED",
            }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={async (payload: TimecardPayload) => {
              // TimecardForm allows null for optional FKs; the service inserts them as-is.
              const newTimecard = await timecardService.addTimecard(
                payload.timecard as Parameters<typeof timecardService.addTimecard>[0],
              );
              toast.showToast({ title: "Timecard created successfully", toastType: "success" });

              // Handle save behavior setting
              const behavior = await getSaveBehavior("timecard");
              switch (behavior) {
                case "view_created":
                  router.setParams({ new: undefined, id: newTimecard?.id });
                  break;
                case "create_another":
                  handleReset();
                  break;
                case "close":
                default:
                  router.setParams({ new: undefined, panel: undefined });
                  break;
              }
            }}
            onDirtyChange={setDirty}
            onStatusChange={setStatus}
          />
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  centered: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 8,
  },
});
